import { DiwaAPI } from './client'
import type { MissionProposal } from './missions'

/** Lifecycle: fresh (unjudged) → seen → acted (became a mission / wrote a fact) | dismissed. */
export type SignalStatus = 'fresh' | 'seen' | 'acted' | 'dismissed'

export interface Signal {
  id: string
  title: string
  type: string
  importance: string
  confidence: string
  related: string[]
  domain: string
  suggested_action: string
  status: SignalStatus
  /** Non-null = lake-synced: dismiss is the durable judgment; hard-delete would resurrect it. */
  source_url: string | null
  created_at: string
}

export interface CreateSignalInput {
  title: string
  type: string
  importance: string
  confidence: string
  related: string[]
  domain: string
  suggested_action: string
}

/** A type this operator reliably sets aside in a domain — claimed only above the
 *  backend's consistency floor, so a thin or mixed record says nothing at all. */
export interface AccountPattern {
  type: string
  dismissed: number
  judged: number
}

/** What your judgments add up to in one domain, since the rules there last changed. */
export interface DomainAccount {
  domain: string
  /** null = no rules written for this domain yet, so the counts are lifetime. */
  rules_updated_at: string | null
  acted: number
  dismissed: number
  /** fresh + seen — nobody has judged these yet, so they are not evidence of anything. */
  waiting: number
  /** A replay engine exists here, so rule-review can show real before/after numbers. */
  backtestable: boolean
  patterns: AccountPattern[]
}

export class SignalsAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  list(): Promise<Signal[]> {
    return this.api.get<Signal[]>('/signals')
  }

  /** The account — arithmetic over your own rows; no lake read, no model. */
  account(): Promise<DomainAccount[]> {
    return this.api.get<DomainAccount[]>('/signals/account')
  }

  /** Summon the rule-smith for one domain. Returns nothing useful — the mission is the point. */
  reviewRules(domain: string): Promise<unknown> {
    return this.api.post<unknown>('/missions/rule-review', { domain })
  }

  create(input: CreateSignalInput): Promise<Signal> {
    return this.api.post<Signal>('/signals', input)
  }

  remove(id: string): Promise<{ deleted: boolean }> {
    return this.api.del<{ deleted: boolean }>(`/signals/${id}`)
  }

  updateStatus(id: string, status: SignalStatus): Promise<Signal> {
    return this.api.patch<Signal>(`/signals/${id}`, { status })
  }

  /** Signal → mission: the composer drafts from the signal's context; you judge the proposal. */
  proposeMission(id: string): Promise<{ proposal: MissionProposal; signal_id: string }> {
    return this.api.post<{ proposal: MissionProposal; signal_id: string }>(
      `/signals/${id}/propose-mission`,
      {},
    )
  }
}
